import React from 'react';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const TermsOfService = () => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 }
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Terms of Service — Engaged Manila"
        description="Read the Engaged Manila terms of service. Understand the terms and conditions for using our influencer marketing platform."
        url="/terms-of-service"
      />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 tracking-tight">
              Terms of <span className="text-primary">Service</span>
            </h1>
            <p className="mt-4 max-w-3xl mx-auto text-xl text-gray-500">
              Last updated: August 1, 2025
            </p>
          </motion.div>
        </div>
      </section>
      
      {/* Content Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Table of Contents */}
          <motion.div
            className="mb-12 bg-gray-50 rounded-xl p-6 border border-gray-200 shadow-sm"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Table of Contents</h2>
            <nav className="grid md:grid-cols-2 gap-2">
              {[
                { id: "agreement", title: "1. Agreement to Terms" },
                { id: "registration", title: "2. User Registration" },
                { id: "representations", title: "3. User Representations" },
                { id: "services", title: "4. Platform Services" },
                { id: "payments", title: "5. Payments and Fees" },
                { id: "ip", title: "6. Intellectual Property Rights" },
                { id: "prohibited", title: "7. Prohibited Activities" },
                { id: "termination", title: "8. Term and Termination" },
                { id: "disclaimer", title: "9. Disclaimer" },
                { id: "liability", title: "10. Limitation of Liability" },
                { id: "governing-law", title: "11. Governing Law" },
                { id: "contact", title: "12. Contact Us" }
              ].map(item => (
                <a 
                  key={item.id} 
                  href={`#${item.id}`} 
                  className="flex items-center py-2 px-3 text-gray-700 hover:bg-gray-100 rounded-md group transition-colors duration-200"
                >
                  <span>{item.title}</span>
                </a>
              ))}
            </nav>
          </motion.div>
          
          <motion.div 
            className="bg-white rounded-xl p-8 shadow-md border border-gray-100 prose prose-lg max-w-none"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
          <motion.div variants={itemVariants} id="agreement" className="mb-12 pb-8 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">1. Agreement to Terms</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              These Terms of Service ("Terms") constitute a legally binding agreement made between you, whether personally or on behalf of an entity ("you") and Engaged Manila ("we," "us" or "our"), concerning your access to and use of the Engaged Manila website and platform (collectively, the "Services").
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              You agree that by accessing the Services, you have read, understood, and agree to be bound by all of these Terms. If you do not agree with all of these Terms, then you are expressly prohibited from using the Services and you must discontinue use immediately.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              We reserve the right, in our sole discretion, to make changes or modifications to these Terms at any time and for any reason. We will alert you about any changes by updating the "Last updated" date of these Terms, and you waive any right to receive specific notice of each such change.
            </p>
          </motion.div>
          
          <motion.div variants={itemVariants} id="registration" className="mb-12 pb-8 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">2. User Registration</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              To access certain features of the Services, you must register for an account. You agree to provide accurate, current, and complete information during the registration process and to update such information to keep it accurate, current, and complete.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              You are responsible for safeguarding the password that you use to access the Services and for any activities or actions under your password. You agree not to disclose your password to any third party. You must notify us immediately upon becoming aware of any breach of security or unauthorized use of your account.
            </p>
          </motion.div>
          
          <motion.div variants={itemVariants} id="representations" className="mb-12 pb-8 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">3. User Representations</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              By using the Services, you represent and warrant that:
            </p>
            <ol className="space-y-2 list-decimal pl-6 mb-4">
              <li className="text-gray-700">You have the legal capacity and agree to comply with these Terms;</li>
              <li className="text-gray-700">You are not a minor in the jurisdiction in which you reside;</li>
              <li className="text-gray-700">You will not access the Services through automated or non-human means;</li>
              <li className="text-gray-700">You will not use the Services for any illegal or unauthorized purpose;</li>
              <li className="text-gray-700">Your use of the Services will not violate any applicable law or regulation.</li>
            </ol>
          </motion.div>
          
          <motion.div variants={itemVariants} id="services" className="mb-12 pb-8 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">4. Platform Services</h2>
            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-4">4.1 For Brands</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Brands may use our Services to create campaigns for product reviews and collaborations with influencers. Brands are responsible for:
            </p>
            <ul className="space-y-2 list-disc pl-6 mb-6">
              <li className="text-gray-700">Providing accurate and complete information about their campaigns;</li>
              <li className="text-gray-700">Setting clear expectations and requirements for influencers;</li>
              <li className="text-gray-700">Reviewing and approving influencer content in a timely manner;</li>
              <li className="text-gray-700">Making payments as agreed upon in campaign terms;</li>
              <li className="text-gray-700">Complying with all applicable laws and regulations, including those related to advertising, disclosure, and consumer protection.</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-4">4.2 For Influencers</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Influencers may use our Services to discover and participate in brand campaigns. Influencers are responsible for:
            </p>
            <ul className="space-y-2 list-disc pl-6 mb-4">
              <li className="text-gray-700">Providing accurate information about their social media accounts, audience demographics, and engagement metrics;</li>
              <li className="text-gray-700">Creating content that meets the requirements specified in campaign briefs;</li>
              <li className="text-gray-700">Making proper disclosures as required by applicable laws and regulations;</li>
              <li className="text-gray-700">Delivering content within agreed-upon timeframes;</li>
              <li className="text-gray-700">Maintaining professional communication with brands.</li>
            </ul>
          </motion.div>
          
          <motion.div variants={itemVariants} id="payments" className="mb-12 pb-8 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">5. Payments and Fees</h2>
            <p>
              We may charge fees for certain aspects of the Services. You agree to pay all fees and charges incurred in connection with your use of the Services.
            </p>
            <p>
              For brands, payment for campaigns must be made in advance. Funds will be held in escrow and released to influencers upon successful completion and approval of content.
            </p>
            <p>
              For influencers, payment will be processed after the brand approves the content and in accordance with the payment terms specified in the campaign.
            </p>
            <p>
              We reserve the right to change our fees at any time. If you do not agree with the fee changes, you have the right to terminate your account.
            </p>
          </motion.div>
          
          <motion.div variants={itemVariants} id="ip" className="mb-12 pb-8 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">6. Intellectual Property Rights</h2>
            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-4">6.1 Our Intellectual Property</h3>
            <p>
              The Services and their entire contents, features, and functionality (including but not limited to all information, software, text, displays, images, video, and audio, and the design, selection, and arrangement thereof) are owned by us, our licensors, or other providers of such material and are protected by copyright, trademark, patent, trade secret, and other intellectual property or proprietary rights laws.
            </p>
            
            <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-4">6.2 User Content</h3>
            <p>
              When you create or make available any content through the Services, you grant us a worldwide, non-exclusive, royalty-free license to use, copy, modify, create derivative works based upon, distribute, publicly display, publicly perform, and distribute your content in connection with operating and providing the Services.
            </p>
            <p>
              You are solely responsible for your content and the consequences of posting or publishing it. By posting or publishing content, you affirm, represent, and warrant that you are the creator and owner of the content or have the necessary licenses, rights, consents, and permissions to use and authorize us to use all intellectual property rights in and to your content.
            </p>
          </motion.div>
          
          <motion.div variants={itemVariants} id="prohibited" className="mb-12 pb-8 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">7. Prohibited Activities</h2>
            <p>
              You may not access or use the Services for any purpose other than that for which we make the Services available. The Services may not be used in connection with any commercial endeavors except those that are specifically endorsed or approved by us.
            </p>
            <p>
              Prohibited activities include, but are not limited to:
            </p>
            <ul>
              <li>Systematic retrieval of data or other content from the Services to create a collection, compilation, database, or directory;</li>
              <li>Making any unauthorized use of the Services, including collecting usernames and/or email addresses of users by electronic or other means;</li>
              <li>Engaging in any automated use of the system, such as using scripts to send comments or messages;</li>
              <li>Using the Services in a manner inconsistent with any applicable laws or regulations;</li>
              <li>Uploading or transmitting viruses, Trojan horses, or other material that interferes with any party's uninterrupted use and enjoyment of the Services;</li>
              <li>Attempting to impersonate another user or person;</li>
              <li>Using any information obtained from the Services in order to harass, abuse, or harm another person;</li>
              <li>Using the Services as part of any effort to compete with us or otherwise use the Services for any revenue-generating endeavor or commercial enterprise.</li>
            </ul>
          </motion.div>
          
          <motion.div variants={itemVariants} id="termination" className="mb-12 pb-8 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">8. Term and Termination</h2>
            <p>
              These Terms shall remain in full force and effect while you use the Services. We may terminate or suspend your account and access to the Services immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach these Terms.
            </p>
            <p>
              Upon termination, your right to use the Services will immediately cease. If you wish to terminate your account, you may simply discontinue using the Services or contact us to request account deletion.
            </p>
            <p>
              All provisions of these Terms which by their nature should survive termination shall survive termination, including, without limitation, ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
            </p>
          </motion.div>
          
          <motion.div variants={itemVariants} id="disclaimer" className="mb-12 pb-8 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">9. Disclaimer</h2>
            <p>
              THE SERVICES ARE PROVIDED ON AN "AS-IS" AND "AS AVAILABLE" BASIS. WE EXPRESSLY DISCLAIM ALL WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
            <p>
              WE MAKE NO WARRANTY THAT THE SERVICES WILL MEET YOUR REQUIREMENTS, BE AVAILABLE ON AN UNINTERRUPTED, TIMELY, SECURE, OR ERROR-FREE BASIS, OR BE ACCURATE, RELIABLE, COMPLETE, LEGAL, OR SAFE.
            </p>
          </motion.div>
          
          <motion.div variants={itemVariants} id="liability" className="mb-12 pb-8 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">10. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL WE BE LIABLE FOR ANY INDIRECT, PUNITIVE, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR EXEMPLARY DAMAGES, INCLUDING WITHOUT LIMITATION DAMAGES FOR LOSS OF PROFITS, GOODWILL, USE, DATA, OR OTHER INTANGIBLE LOSSES, ARISING OUT OF OR RELATING TO YOUR USE OF, OR INABILITY TO USE, THE SERVICES.
            </p>
          </motion.div>
          
          <motion.div variants={itemVariants} id="governing-law" className="mb-12 pb-8 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">11. Governing Law</h2>
            <p>
              These Terms shall be governed by and defined following the laws of the Philippines. Engaged Manila and yourself irrevocably consent that the courts of the Philippines shall have exclusive jurisdiction to resolve any dispute which may arise in connection with these Terms.
            </p>
          </motion.div>
          
          <motion.div variants={itemVariants} id="contact" className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">12. Contact Us</h2>
            <p>
              If you have any questions about these Terms of Service, please contact us:
            </p>
            <ul className="space-y-2 list-disc pl-6 mb-4">
              <li className="text-gray-700">By email: <a href="mailto:hola@engagedmanila.com" className="text-primary hover:text-primary-dark transition-colors">hola@engagedmanila.com</a></li>
              <li className="text-gray-700">By phone: +632 835 26129</li>
              <li className="text-gray-700">By mail: 785 Aurora Boulevard, Cubao, Quezon City, Metro Manila, Philippines</li>
            </ul>
          </motion.div>
          </motion.div>
          
          {/* Footer note */}
          <motion.div 
            className="mt-16 pt-8 border-t border-gray-200 text-center text-gray-500 text-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <p>© {new Date().getFullYear()} Engaged Manila. All rights reserved.</p>
            <p className="mt-1">Last updated: August 1, 2025</p>
          </motion.div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default TermsOfService;
